<template>
    <div>
        <NavbarOne/>

        <div class="bg-[#F8F5F0] dark:bg-dark-secondary py-5 md:py-[30px]">
            <div class="container-fluid">
                <ul class="flex items-center gap-[10px] text-base md:text-lg leading-none font-normal text-title dark:text-white max-w-[1720px] mx-auto flex-wrap">
                    <li><router-link to="/index-v2">Home</router-link></li>
                    <li>/</li>
                    <li><router-link to="/shop-v2">Shop</router-link></li>
                    <li>/</li>
                    <li class="text-primary">{{ product?.name || 'Product Details' }}</li>
                </ul>
            </div>
        </div>

        <div class="s-py-50" data-aos="fade-up">
            <div class="container-fluid">
                <div v-if="loading" class="max-w-[1720px] mx-auto py-16 text-center text-base md:text-lg text-paragraph dark:text-white-light">
                    Loading product...
                </div>
                <div v-else-if="error" class="max-w-[1720px] mx-auto py-16 text-center text-base md:text-lg text-[#E13939]">
                    {{ error }}
                </div>
                <div v-else-if="product" class="max-w-[1720px] mx-auto flex justify-between gap-10 flex-col lg:flex-row">
                    <div class="w-full lg:w-[45%]">
                        <div class="relative">
                            <button
                                v-if="product.saleTag || product.is_featured"
                                class="absolute top-5 left-0 p-2 bg-primary text-lg leading-none text-white font-medium z-50"
                            >
                                {{ product.saleTag || 'Featured' }}
                            </button>
                            <div class="relative w-full h-[320px] sm:h-[400px] lg:h-[460px] bg-[#F6F2EC] dark:bg-title/40 overflow-hidden">
                                <img :src="currentImage" :alt="product.name" class="w-full h-full object-cover object-center">
                                <button
                                    v-if="galleryImages.length > 1"
                                    type="button"
                                    aria-label="Previous image"
                                    class="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/85 dark:bg-title/85 text-title dark:text-white shadow-md backdrop-blur-sm transition duration-300 hover:bg-primary hover:text-white z-10"
                                    @click="prevImage"
                                >
                                    <i class="mdi mdi-chevron-left text-[24px]"></i>
                                </button>
                                <button
                                    v-if="galleryImages.length > 1"
                                    type="button"
                                    aria-label="Next image"
                                    class="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/85 dark:bg-title/85 text-title dark:text-white shadow-md backdrop-blur-sm transition duration-300 hover:bg-primary hover:text-white z-10"
                                    @click="nextImage"
                                >
                                    <i class="mdi mdi-chevron-right text-[24px]"></i>
                                </button>
                            </div>
                            <div class="mt-3 flex flex-wrap gap-2" v-if="galleryImages.length > 1">
                                <img
                                    v-for="(image, index) in galleryImages"
                                    :key="image"
                                    :src="image"
                                    :alt="`${product.name} ${index + 1}`"
                                    class="w-[64px] h-[64px] object-cover cursor-pointer border-2 duration-300"
                                    :class="activeImageIndex === index ? 'border-primary' : 'border-transparent'"
                                    @click="activeImageIndex = index"
                                >
                            </div>
                            <button
                                v-if="product.hasAr"
                                type="button"
                                class="btn btn-outline btn-sm w-full mt-4"
                                data-text="View in your room"
                                @click="arViewerOpen = true"
                            >
                                <span><i class="mdi mdi-cube-scan mr-2 text-[20px] align-[-3px]"></i>View in your room</span>
                            </button>
                            <router-link :to="{ path: '/assistant', query: { product: product.id } }" class="mt-3 block text-center text-sm text-paragraph underline underline-offset-4 hover:text-primary dark:text-white-light">
                                Not sure it suits your room? Ask the assistant with a photo
                            </router-link>
                        </div>
                    </div>

                    <div class="lg:max-w-[635px] w-full">
                        <div class="pb-4 sm:pb-6 border-b border-bdr-clr dark:border-bdr-clr-drk">
                            <h2 class="font-semibold leading-none">{{ product.name }}</h2>
                            <div class="flex gap-4 items-center mt-[15px] flex-wrap">
                                <span class="text-2xl sm:text-3xl text-primary leading-none block">{{ product.formattedPrice }}</span>
                                <span v-if="product.formattedOriginalPrice" class="text-lg sm:text-xl leading-none text-paragraph line-through dark:text-white-light">
                                    {{ product.formattedOriginalPrice }}
                                </span>
                                <span
                                    v-if="product.stockLabel"
                                    class="inline-flex items-center rounded-full px-3 py-2 text-sm font-medium"
                                    :class="product.inStock
                                        ? 'bg-[#F8F8F9] dark:bg-title text-title dark:text-white'
                                        : 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200'"
                                >
                                    {{ product.stockLabel }}
                                </span>
                                <span
                                    v-if="product.inStock && product.stockQuantity <= 3"
                                    class="text-sm text-primary"
                                >
                                    Only {{ product.stockQuantity }} left
                                </span>
                            </div>

                            <p class="sm:text-lg mt-5 md:mt-7">
                                {{ product.description || product.shortDescription || 'This product is available through the live catalog.' }}
                            </p>
                        </div>

                        <div class="py-4 sm:py-6 border-b border-bdr-clr dark:border-bdr-clr-drk" data-aos="fade-up" data-aos-delay="200">
                            <div v-if="modifierGroups.length" class="pm-groups">
                                <OptionGroups
                                    :groups="modifierGroups"
                                    :is-selected="isOptionSelected"
                                    :summary="selectedModifierSummary"
                                    :adjustment="selectedAdjustmentFor"
                                    :signed-price="signedPrice"
                                    @toggle="toggleModifierSelection"
                                />

                                <div v-if="selectedAdjustmentTotal" class="pm-total">
                                    <span>Price with your options</span>
                                    <span class="pm-total__amount">{{ formatPrice(configuredPrice) }}</span>
                                </div>
                            </div>
                            <IncDec v-model="quantity"/>
                            <p v-if="selectionError" class="mt-4 text-sm text-[#E13939]">{{ selectionError }}</p>
                            <div class="flex gap-4 mt-4 sm:mt-6 flex-wrap">
                                <button
                                    type="button"
                                    class="btn btn-solid"
                                    :class="{ 'opacity-50 cursor-not-allowed': !product.inStock }"
                                    :disabled="!product.inStock"
                                    :data-text="product.inStock ? 'Add to Cart' : 'Out of Stock'"
                                    @click="addToCart"
                                >
                                    <span>{{ product.inStock ? 'Add to Cart' : 'Out of Stock' }}</span>
                                </button>
                                <button type="button" class="btn btn-outline" data-text="Request Quote" @click="requestQuote">
                                    <span>Request Quote</span>
                                </button>
                                <button type="button" class="btn btn-outline" :data-text="wishlistLabel" @click="toggleWishlist">
                                    <span>{{ wishlistLabel }}</span>
                                </button>
                            </div>
                        </div>

                        <div class="py-4 sm:py-6 border-b border-bdr-clr dark:border-bdr-clr-drk" data-aos="fade-up" data-aos-delay="300">
                            <div class="flex gap-x-12 gap-y-3 flex-wrap">
                                <h6 class="leading-none font-medium">SKU : {{ product.sku || 'N/A' }}</h6>
                                <h6 class="leading-none font-medium">Category : {{ product.categoryName || 'Uncategorized' }}</h6>
                                <h6 v-if="product.subCategoryName" class="leading-none font-medium">Subcategory : {{ product.subCategoryName }}</h6>
                            </div>
                            <div class="flex gap-x-12 lg:gap-x-24 gap-y-3 flex-wrap mt-5 sm:mt-10">
                                <div class="flex gap-[10px] items-center" v-if="product.dimensions">
                                    <h6 class="leading-none font-medium">Dimensions :</h6>
                                    <span class="text-paragraph dark:text-white-light">{{ product.dimensions }}</span>
                                </div>
                                <div class="flex gap-[10px] items-center" v-if="product.weight">
                                    <h6 class="leading-none font-medium">Weight :</h6>
                                    <span class="text-paragraph dark:text-white-light">{{ product.weight }}</span>
                                </div>
                                <div class="flex gap-[10px] items-center">
                                    <h6 class="leading-none font-medium">Customizable :</h6>
                                    <span class="text-paragraph dark:text-white-light">{{ product.isCustomizable ? 'Yes' : 'No' }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="py-4 sm:py-6 border-b border-bdr-clr dark:border-bdr-clr-drk" data-aos="fade-up" data-aos-delay="400">
                            <h4 class="font-medium leading-none">Description</h4>
                            <div class="flex flex-wrap gap-[10px] md:gap-[15px] mt-5 md:mt-6">
                                <span class="btn btn-theme-outline btn-xs"><span>{{ product.categoryName || 'Catalog' }}</span></span>
                                <span v-if="product.subCategoryName" class="btn btn-theme-outline btn-xs"><span>{{ product.subCategoryName }}</span></span>
                                <span v-if="product.sku" class="btn btn-theme-outline btn-xs"><span>{{ product.sku }}</span></span>
                            </div>
                        </div>

                        <div class="pt-4 sm:pt-6" data-aos="fade-up" data-aos-delay="500">
                            <div class="flex items-center gap-6">
                                <h6 class="font-normal">Share :</h6>
                                <div class="flex gap-6 items-center">
                                    <a :href="shareLinks.whatsapp" target="_blank" rel="noopener" title="Share on WhatsApp" aria-label="Share on WhatsApp" class="text-paragraph duration-300 dark:text-white hover:text-primary dark:hover:text-primary">
                                        <i class="fa-brands fa-whatsapp"></i>
                                    </a>
                                    <a :href="shareLinks.facebook" target="_blank" rel="noopener" title="Share on Facebook" aria-label="Share on Facebook" class="text-paragraph duration-300 dark:text-white hover:text-primary dark:hover:text-primary">
                                        <i class="fa-brands fa-facebook-f"></i>
                                    </a>
                                    <a :href="shareLinks.email" title="Share by email" aria-label="Share by email" class="text-paragraph duration-300 dark:text-white hover:text-primary dark:hover:text-primary">
                                        <i class="fa-solid fa-envelope"></i>
                                    </a>
                                    <button type="button" title="Copy link" aria-label="Copy link" class="text-paragraph duration-300 dark:text-white hover:text-primary dark:hover:text-primary" @click="copyLink">
                                        <i class="fa-solid fa-link"></i>
                                    </button>
                                    <span v-if="copied" class="text-sm text-primary">Link copied</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="s-py-50">
            <DetailTab :product="product"/>
        </div>

        <div class="s-py-50-100" v-if="relatedProducts.length">
            <div class="container-fluid">
                <div class="max-w-[547px] mx-auto text-center">
                    <h6 class="text-2xl sm:text-3xl md:text-4xl leading-none">Related Products</h6>
                    <p class="mt-3">Explore more pieces from the same collection and keep the product ids flowing from the live catalog.</p>
                </div>
                <LayoutOne :classList="'max-w-[1720px] mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8 pt-8 md:pt-[50px]'" :productList="relatedProducts"/>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>

        <ArViewer
            v-if="arViewerOpen && product?.hasAr"
            :glb-url="product.modelGlbUrl"
            :usdz-url="product.modelUsdzUrl"
            :poster="currentImage"
            :name="product.name"
            :placement="product.arPlacement"
            :scale="product.arScale"
            @close="arViewerOpen = false"
        />
    </div>
</template>

<script setup>
    import { computed, onMounted, reactive, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import IncDec from '@/components/inc-dec.vue';
    import DetailTab from '@/components/product/detail-tab.vue';
    import LayoutOne from '@/components/product/layout-one.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import Aos from 'aos';
    import ArViewer from '@/components/product/ar-viewer.vue';
import OptionGroups from '@/components/product/option-groups.vue';
    import { formatPrice, mapProductAr, mapProductCard, storefrontRequest } from '@/lib/storefront-api';
    import { seoDefaults, setSeo } from '@/lib/seo';
    import { PRODUCT_PLACEHOLDER } from '@/lib/placeholder';
    import { useCart } from '@/composables/use-cart';
    import { useClientSession } from '@/composables/use-client-session';
    import { useWishlist } from '@/composables/use-wishlist';

    const QUOTE_PREFILL_STORAGE_KEY = 'quote-product-prefill';
    const route = useRoute();
    const router = useRouter();
    const cart = useCart();
    const session = useClientSession();
    const wishlist = useWishlist();
    const loading = ref(true);
    const error = ref('');
    const product = ref(null);
    const relatedProducts = ref([]);
    const activeImageIndex = ref(0);
    const quantity = ref(1);
    const selectionError = ref('');
    const copied = ref(false);
    const arViewerOpen = ref(false);

    // Real share targets instead of dead icons. WhatsApp leads because that is how
    // most customers here send a piece to someone.
    const shareLinks = computed(() => {
        const url = typeof window !== 'undefined' ? window.location.href : '';
        const title = product.value?.name || 'Sweet Home Gallery';
        const message = `${title} — ${url}`;

        return {
            whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(message)}`,
        };
    });

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            copied.value = true;
            setTimeout(() => { copied.value = false; }, 2000);
        } catch (error) {
            // Clipboard is blocked on insecure origins; the other share targets still work.
        }
    }
    const selectedModifiers = reactive({});

    const galleryImages = computed(() => {
        if (!product.value) {
            return [];
        }

        const images = [
            product.value.main_image_url,
            ...(product.value.gallery_image_urls || []),
        ].filter(Boolean);

        return [...new Set(images)];
    });

    const currentImage = computed(() => galleryImages.value[activeImageIndex.value] || PRODUCT_PLACEHOLDER);

    function prevImage() {
        const total = galleryImages.value.length;
        if (total <= 1) return;
        activeImageIndex.value = (activeImageIndex.value - 1 + total) % total;
    }

    function nextImage() {
        const total = galleryImages.value.length;
        if (total <= 1) return;
        activeImageIndex.value = (activeImageIndex.value + 1) % total;
    }
    const modifierGroups = computed(() => product.value?.modifiers || []);
    const isWishlisted = computed(() => product.value ? wishlist.isInWishlist(product.value.id) : false);
    const wishlistLabel = computed(() => isWishlisted.value ? 'Saved to Wishlist' : 'Add to Wishlist');

    function mapProductDetail(item) {
        const dimensionParts = [item.width, item.height, item.depth]
            .filter((value) => value !== null && value !== undefined)
            .map((value) => Number(value));

        return {
            ...item,
            effectivePrice: item.sale_price && Number(item.sale_price) < Number(item.base_price || 0)
                ? Number(item.sale_price)
                : Number(item.base_price || 0),
            formattedPrice: formatPrice(item.sale_price && Number(item.sale_price) < Number(item.base_price || 0) ? item.sale_price : item.base_price),
            formattedOriginalPrice: item.sale_price && Number(item.sale_price) < Number(item.base_price || 0) ? formatPrice(item.base_price) : '',
            saleTag: item.sale_price && Number(item.sale_price) < Number(item.base_price || 0)
                ? `${Math.round(((Number(item.base_price) - Number(item.sale_price)) / Number(item.base_price)) * 100)}% OFF`
                : '',
            shortDescription: item.short_description || '',
            categoryName: item.category?.name || '',
            subCategoryName: item.subCategory?.name || item.sub_category?.name || '',
            isCustomizable: Boolean(item.is_customizable),
            ...mapProductAr(item),
            stockQuantity: Number(item.stock_quantity || 0),
            inStock: Number(item.stock_quantity || 0) > 0,
            stockLabel: Number(item.stock_quantity || 0) > 0 ? 'In stock' : 'Out of stock',
            dimensions: dimensionParts.length ? `${dimensionParts.join(' x ')} ${item.dimension_unit || ''}`.trim() : '',
            weight: item.weight ? `${Number(item.weight)} ${item.weight_unit || ''}`.trim() : '',
            modifiers: (item.modifiers || []).map((modifier) => ({
                id: modifier.id,
                name: modifier.name,
                selection_type: modifier.selection_type || 'single',
                is_required: Boolean(modifier.is_required),
                is_color: Boolean(modifier.is_color),
                options: (modifier.options || []).map((option) => ({
                    id: option.id,
                    label: option.label,
                    value: option.value || '',
                    level: option.level || '',
                    is_default: Boolean(option.is_default),
                    price_adjustment: Number(option.price_adjustment || 0),
                    image_url: option.image_url || '',
                })),
            })),
        };
    }

    function initializeModifierSelections(nextProduct) {
        Object.keys(selectedModifiers).forEach((key) => {
            delete selectedModifiers[key];
        });

        (nextProduct?.modifiers || []).forEach((modifier) => {
            const defaultOptions = modifier.options.filter((option) => option.is_default);

            if (modifier.selection_type === 'multiple') {
                selectedModifiers[modifier.id] = defaultOptions.map((option) => option.id);
                return;
            }

            selectedModifiers[modifier.id] = defaultOptions[0]?.id || null;
        });
    }

    function isOptionSelected(modifierId, optionId) {
        const currentSelection = selectedModifiers[modifierId];

        if (Array.isArray(currentSelection)) {
            return currentSelection.includes(optionId);
        }

        return currentSelection === optionId;
    }

    function toggleModifierSelection(modifier, option) {
        selectionError.value = '';

        if (modifier.selection_type === 'multiple') {
            const currentSelection = Array.isArray(selectedModifiers[modifier.id]) ? [...selectedModifiers[modifier.id]] : [];
            const optionIndex = currentSelection.indexOf(option.id);

            if (optionIndex >= 0) {
                currentSelection.splice(optionIndex, 1);
            } else {
                currentSelection.push(option.id);
            }

            selectedModifiers[modifier.id] = currentSelection;
            return;
        }

        selectedModifiers[modifier.id] = option.id;
    }



    function selectedModifierSummary(modifier) {
        const currentSelection = selectedModifiers[modifier.id];
        const selectedOptionIds = Array.isArray(currentSelection)
            ? currentSelection
            : currentSelection
                ? [currentSelection]
                : [];

        return modifier.options
            .filter((option) => selectedOptionIds.includes(option.id))
            .map((option) => option.label)
            .join(', ');
    }

    function selectedOptionsFor(modifier) {
        const currentSelection = selectedModifiers[modifier.id];
        const selectedOptionIds = Array.isArray(currentSelection)
            ? currentSelection
            : currentSelection
                ? [currentSelection]
                : [];

        return modifier.options.filter((option) => selectedOptionIds.includes(option.id));
    }

    function selectedAdjustmentFor(modifier) {
        return selectedOptionsFor(modifier).reduce((total, option) => total + Number(option.price_adjustment || 0), 0);
    }

    const selectedAdjustmentTotal = computed(() =>
        modifierGroups.value.reduce((total, modifier) => total + selectedAdjustmentFor(modifier), 0)
    );

    const configuredPrice = computed(() => Number(product.value?.effectivePrice || 0) + selectedAdjustmentTotal.value);

    // "+$95" / "−$40": whole amounts drop the cents so the cards stay short.
    function signedPrice(value) {
        const amount = Number(value || 0);
        const absolute = Math.abs(amount);
        const formatted = Number.isInteger(absolute)
            ? formatPrice(absolute).replace(/\.00$/, '')
            : formatPrice(absolute);

        return `${amount < 0 ? '−' : '+'}${formatted}`;
    }

    function buildSelectedModifiersPayload() {
        return modifierGroups.value.reduce((payload, modifier) => {
            const currentSelection = selectedModifiers[modifier.id];
            const selectedOptionIds = Array.isArray(currentSelection)
                ? currentSelection
                : currentSelection
                    ? [currentSelection]
                    : [];

            if (!selectedOptionIds.length) {
                return payload;
            }

            const options = modifier.options
                .filter((option) => selectedOptionIds.includes(option.id))
                .map((option) => ({
                    option_id: option.id,
                    label: option.label,
                    value: option.value,
                    price_adjustment: Number(option.price_adjustment || 0),
                }));

            if (!options.length) {
                return payload;
            }

            payload.push({
                modifier_id: modifier.id,
                modifier_name: modifier.name,
                selection_type: modifier.selection_type,
                is_color: modifier.is_color,
                options,
            });

            return payload;
        }, []);
    }

    function validateSelections() {
        const missingModifier = modifierGroups.value.find((modifier) => {
            if (!modifier.is_required) {
                return false;
            }

            const currentSelection = selectedModifiers[modifier.id];
            return Array.isArray(currentSelection) ? !currentSelection.length : !currentSelection;
        });

        if (missingModifier) {
            selectionError.value = `Please select ${missingModifier.name.toLowerCase()}.`;
            return false;
        }

        selectionError.value = '';
        return true;
    }

    function addToCart() {
        if (!product.value || !validateSelections()) {
            return;
        }

        if (!product.value.inStock) {
            selectionError.value = 'This piece is out of stock. Request a quote and we will build it for you.';
            return;
        }

        if (quantity.value > product.value.stockQuantity) {
            selectionError.value = `Only ${product.value.stockQuantity} left in stock.`;
            return;
        }

        const modifiersPayload = buildSelectedModifiersPayload();

        cart.addItem({
            product_id: product.value.id,
            name: product.value.name,
            sku: product.value.sku,
            image: currentImage.value,
            category_name: product.value.categoryName,
            quantity: quantity.value,
            base_price: Number(product.value.effectivePrice || product.value.base_price || 0),
            stock_quantity: product.value.stockQuantity,
            selected_modifiers: modifiersPayload,
        });

        router.push('/cart');
    }

    function requestQuote() {
        if (!product.value || !validateSelections()) {
            return;
        }

        const modifiersPayload = buildSelectedModifiersPayload();
        const selectedSummary = modifiersPayload
            .map((modifier) => `${modifier.modifier_name}: ${(modifier.options || []).map((option) => option.label).join(', ')}`)
            .join(' | ');

        // Rough item type from the category name so the quotation form asks the right questions.
        const category = `${product.value.categoryName || ''} ${product.value.subCategoryName || ''}`.toLowerCase();
        const itemType = /sofa|seat|chair|armchair|stool|bench|ottoman/.test(category) ? 'seating'
            : /table|desk|dining|office/.test(category) ? 'tables'
            : /bed|mattress|wardrobe|nightstand/.test(category) ? 'bedroom'
            : /storage|shelf|cabinet|unit|tv|bookcase/.test(category) ? 'storage'
            : /outdoor|garden|patio/.test(category) ? 'outdoor'
            : '';

        sessionStorage.setItem(QUOTE_PREFILL_STORAGE_KEY, JSON.stringify({
            product_id: product.value.id,
            name: product.value.name,
            sku: product.value.sku || '',
            image: galleryImages.value[0] || '',
            title: product.value.name,
            item_type: itemType,
            quantity: quantity.value,
            desired_width: product.value.width ?? '',
            desired_height: product.value.height ?? '',
            desired_depth: product.value.depth ?? '',
            dimension_unit: product.value.dimension_unit || 'cm',
            selected_modifiers: modifiersPayload,
            selected_summary: selectedSummary,
        }));

        router.push({
            path: '/request-quotation',
            query: {
                quote_prefill: 'product-page',
            },
        });
    }

    async function toggleWishlist() {
        if (!product.value) {
            return;
        }

        if (!session.isAuthenticated.value) {
            router.push('/login');
            return;
        }

        if (wishlist.isInWishlist(product.value.id)) {
            await wishlist.remove(product.value.id);
            return;
        }

        await wishlist.add(product.value.id);
    }

    async function loadRelatedProducts(item) {
        if (!item?.category_id) {
            relatedProducts.value = [];
            return;
        }

        const response = await storefrontRequest('/products', {
            params: {
                category_id: item.category_id,
                per_page: 4,
            },
        });

        relatedProducts.value = (response.data || [])
            .filter((productItem) => productItem.id !== item.id)
            .slice(0, 4)
            .map(mapProductCard);
    }

    async function loadProduct() {
        loading.value = true;
        error.value = '';
        activeImageIndex.value = 0;
        arViewerOpen.value = false;

        try {
            const response = await storefrontRequest(`/products/${route.params.id}`);
            product.value = mapProductDetail(response.data);
            // The router set a generic "Product" title before this request went out.
            // Now that the piece is known, describe it — this is the title a shared
            // link unfurls with, and the one a search result would show.
            setSeo({
                title: product.value.name,
                description: product.value.shortDescription
                    || `${product.value.name} — made to order by ${seoDefaults.siteName}.`,
                image: product.value.main_image_url || product.value.gallery_image_urls?.[0],
                path: `/product-details/${route.params.id}`,
                type: 'product',
            });
            quantity.value = 1;
            initializeModifierSelections(product.value);
            await loadRelatedProducts(response.data);
        } catch (loadError) {
            product.value = null;
            relatedProducts.value = [];
            error.value = loadError.message || 'Unable to load this product right now.';
        } finally {
            loading.value = false;
        }
    }

    watch(() => route.params.id, async () => {
        await loadProduct();
    });

    onMounted(async () => {
        Aos.init();
        try {
            await session.initializeSession();
        } catch (error) {
            // Invalid tokens are cleared by the session composable.
        }
        if (session.isAuthenticated.value) {
            await wishlist.load();
        }
        await loadProduct();
    });
</script>

<style scoped>
/* Product options wrapper + running total; the option cards live in OptionGroups. */
.pm-groups {
    display: grid;
    gap: 22px;
    margin-bottom: 24px;
}

.pm-total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;
    background: #f8f5f0;
    font-size: 13px;
    color: #3c474e;
}

.pm-total__amount {
    font-size: 18px;
    font-weight: 600;
    color: #172430;
}
</style>

<style>
.dark .pm-total { background: #1e2a35; color: #cfd4d8; }
.dark .pm-total__amount { color: #fff; }
</style>
